import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PANEL_NAV_GROUPS, PANEL_SALONS, PanelSalonConfig } from '../config/panelNavConfig';

const salonAccentActive: Record<PanelSalonConfig['accent'], string> = {
  amber: 'border-amber-500/40 bg-amber-500/15 text-amber-50',
  sky: 'border-sky-500/40 bg-sky-500/15 text-sky-50',
  yellow: 'border-yellow-500/40 bg-yellow-500/15 text-yellow-50',
  violet: 'border-violet-500/40 bg-violet-500/15 text-violet-50',
  cyan: 'border-cyan-500/40 bg-cyan-500/15 text-cyan-50',
};

type PanelSidebarNavProps = {
  roleId: number;
  accessLoading: boolean;
  hasAccessToSection: (section: string) => boolean;
};

function NavIcon({ name }: { name: string }) {
  const cls = 'h-[18px] w-[18px] shrink-0 opacity-90';
  switch (name) {
    case 'dashboard':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 11.5 12 4l9 7.5M5 10v9h14v-9" />
        </svg>
      );
    case 'users':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m18 0v-2a4 4 0 0 0-3-3.87M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      );
    case 'shield':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="m12 3 7 4v5c0 5-3.5 8.5-7 9-3.5-.5-7-4-7-9V7l7-4Z" />
        </svg>
      );
    case 'cta':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13h14M12 5v14M4 9h16M6 19h12" />
        </svg>
      );
    case 'list':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
      );
    case 'logs':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 5h16v14H4zM8 9h8M8 13h8" />
        </svg>
      );
    case 'settings':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.5 3h3l.6 2.3a6.7 6.7 0 0 1 1.5.9l2.2-.8 1.5 2.6-1.7 1.6c.1.5.1 1 .1 1.5s0 1-.1 1.5l1.7 1.6-1.5 2.6-2.2-.8c-.5.4-1 .7-1.5.9L13.5 21h-3l-.6-2.3a6.7 6.7 0 0 1-1.5-.9l-2.2.8-1.5-2.6 1.7-1.6a9 9 0 0 1 0-3L4.7 9.8l1.5-2.6 2.2.8c-.5-.4 1-.7 1.5-.9L10.5 3Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
        </svg>
      );
    default:
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16v12H4z" />
        </svg>
      );
  }
}

const itemIcon: Record<string, string> = {
  Dashboard: 'dashboard',
  'Liste & comptes': 'users',
  'Utilisateurs (détail)': 'users',
  'Statut premium (joueurs)': 'users',
  Modération: 'shield',
  'CTA — interventions': 'cta',
  'Créer un scénario': 'list',
  'Gérer les scénarios': 'list',
  'Catégories mission': 'list',
  'Rôles utilisateurs': 'shield',
  'Accès par défaut (rôles)': 'settings',
  'Logs sécurité (complet)': 'logs',
  'Logs & traçabilité': 'logs',
  'Configuration métier': 'settings',
  Maintenance: 'settings',
  Sauvegardes: 'settings',
  'Référent CM': 'shield',
  'Rôles individuels': 'shield',
};

const PanelSidebarNav: React.FC<PanelSidebarNavProps> = ({ roleId, accessLoading, hasAccessToSection }) => {
  const location = useLocation();

  const canSee = (section: string, fallback: (r: number) => boolean) => {
    if (accessLoading) return fallback(roleId);
    return hasAccessToSection(section);
  };

  const visibleSalons = PANEL_SALONS.filter((s) => canSee(s.section, s.loadingFallback));

  return (
    <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 pb-2 pt-4">
      {PANEL_NAV_GROUPS.map((group) => {
        const items = group.items.filter((item) => canSee(item.section, item.defaultAccess));
        if (!items.length) return null;
        return (
          <div key={group.id}>
            <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {items.map((item) => {
                const prefix = item.activePrefix || item.to;
                const active = location.pathname === item.to || location.pathname.startsWith(`${prefix}/`);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium transition-colors ${
                        active
                          ? 'bg-[#3d4fdb] text-white shadow-[0_0_24px_rgba(61,79,219,0.35)]'
                          : 'text-slate-300 hover:bg-[#141e3a] hover:text-white'
                      }`}
                    >
                      <NavIcon name={itemIcon[item.label] || 'default'} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      {visibleSalons.length > 0 ? (
        <div className="border-t border-[#1e2d4d] pt-4">
          <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Espaces équipe
          </p>
          <ul className="space-y-1">
            {visibleSalons.map((salon) => {
              const active = location.pathname.startsWith(salon.to);
              return (
                <li key={salon.id}>
                  <Link
                    to={salon.to}
                    className={`block rounded-lg border px-3 py-2.5 transition-colors ${
                      active ? salonAccentActive[salon.accent] : 'border-transparent text-slate-300 hover:bg-[#141e3a] hover:text-white'
                    }`}
                  >
                    <span className="block text-[13px] font-semibold">{salon.label}</span>
                    <span className="mt-0.5 block text-[10px] opacity-80">{salon.subtitle}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </nav>
  );
};

export default PanelSidebarNav;
